import axios from "axios";
import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import ChatHistory from "./ChatHistory";
import HeroLanding from "./Landing";
import SegmentOutput from "./SegmentOutput";
import XaiOutput from "./XaiOutput";
import FollowUpOutput from "./FollowUpOutput";
import AuthService from "../../services/AuthService";
import { useSubscription } from "../../hooks/useSubscription";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CardActions,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Chip,
  Skeleton,
  useMediaQuery,
  Paper,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageIcon from "@mui/icons-material/Image";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SchoolIcon from "@mui/icons-material/School";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import TourGuide from "../../../features/components/TourGuide";
import KnowledgeGraph from "./KnowledgeGraph";

const API_BASE_URL = process.env.REACT_APP_API_URL;

interface ChatEntry {
  role: "user" | "bot";
  text: string;
}

export default function ChatDomain() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate(); // Add the navigate hook
  
  const [chatHistory, setChatHistory] = useState<ChatEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [stage, setStage] = useState<
    "start" | "uploaded" | "segmented" | "explained" | "followup"
  >("start");

  const [returnedSegment, setReturnedSegment] = useState<string>("");
  const [xairesponse, setXairesponse] = useState<string>("");
  const [followup, setFollowup] = useState<string>("");
  const [returnedFollowUp, setreturnedFollowUp] = useState<string>("");
  const [xaibot, setXaiBot] = useState<string>("");
  const [caseId, setCaseId] = useState<string | null>(null);
  const [analysisStarted, setAnalysisStarted] = useState<boolean>(false);
  const [analysisComplete, setAnalysisComplete] = useState<boolean>(false);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraLoading, setCameraLoading] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [frontCamera, setFrontCamera] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [cameraInitAttempts, setCameraInitAttempts] = useState<number>(0);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState<number>(0);

  // NER RELATED STATES
  const [extractedEntities, setExtractedEntities] = useState<any[]>([]);
  const [isExtractingEntities, setIsExtractingEntities] = useState<boolean>(false);
  const [entityExtractionComplete, setEntityExtractionComplete] = useState<boolean>(false);
  const [structuredExtraction, setStructuredExtraction] = useState<any>(null);
  const [isExtractingStructured, setIsExtractingStructured] = useState(false);
  const [structuredExtractionComplete, setStructuredExtractionComplete] = useState(false);

  // Free tier usage tracking
  const { imageCount, imageLimit, remaining, canUploadMore, isFreeUser, refreshUsage } = useSubscription();
  
  // Get default limit from env (or fallback to 15)
  const defaultLimit = parseInt(process.env.REACT_APP_LIMIT || '15', 10);
  
  // Calculate warning threshold dynamically (20% of limit or less)
  const warningThreshold = Math.ceil((imageLimit || defaultLimit) * 0.2);
  const isAtLimit = (remaining ?? 0) === 0;
  const isNearLimit = (remaining ?? 0) <= warningThreshold && (remaining ?? 0) > 0;

  // Debug logging for limit state
  useEffect(() => {
    console.log('[📊 ChatDomain Usage State]', {
      imageCount,
      imageLimit,
      remaining,
      canUploadMore,
      isFreeUser,
      isAtLimit,
      isNearLimit,
      warningThreshold,
      shouldShowButtons: isFreeUser && isAtLimit
    });
  }, [imageCount, imageLimit, remaining, canUploadMore, isFreeUser, isAtLimit, isNearLimit, warningThreshold]);

  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatHistoryRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // #### PATIENT HANDLDER####
  const { patientId: urlPatientId } = useParams();
  const [searchParams] = useSearchParams();

  const queryPatientId = searchParams.get("patientId");

  const patientId = urlPatientId || queryPatientId;

  const scrollToBottom = () => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  // Refresh usage count when component mounts (user opens upload page)
  useEffect(() => {
    console.log('[🔄] ChatDomain mounted - refreshing usage count');
    if (refreshUsage) {
      refreshUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Empty deps - only run on mount

  useEffect(() => {
    scrollToBottom();
  }, [stage]);

  // Debugging effect for analysisComplete state
  useEffect(() => {
    console.log(`analysisComplete state changed to: ${analysisComplete}, stage: ${stage}`);
  }, [analysisComplete, stage]);

  // Refresh usage count after successful upload (when analysis completes)
  useEffect(() => {
    if (analysisComplete && refreshUsage) {
      console.log('[🔄] Analysis complete - refreshing usage count');
      refreshUsage();
    }
  }, [analysisComplete, refreshUsage]);

  const extractContent = (htmlString: string) => {
    const paragraphMatch = htmlString.match(/<p>([\s\S]*?)<\/p>/);
    const paragraphContent = paragraphMatch ? paragraphMatch[1] : "";
    return { paragraphContent };
  };

  const resetAnalysis = () => {
    setFile(null);
    setImagePreviewUrl(null);
    setStage("start");
    setChatHistory([]);
    setFollowup("");
    setXairesponse("");
    setReturnedSegment("");
    setreturnedFollowUp("");
    setXaiBot("");
    setCaseId(null);
    setAnalysisStarted(false);
    setAnalysisComplete(false);
    setSelectedFileName("");
    
    setExtractedEntities([]);
    setIsExtractingEntities(false);
    setEntityExtractionComplete(false);

    setStructuredExtraction(null);
    setIsExtractingStructured(false);
    setStructuredExtractionComplete(false);

    // Refresh usage count when starting a new analysis
    console.log('[🔄] Resetting analysis - refreshing usage count');
    if (refreshUsage) {
      refreshUsage();
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Check if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      setIsMobile(
        /android|webos|iphone|ipad|ipod|blackberry|windows phone/.test(userAgent)
      );
    };
    
    checkMobile();
  }, []);


  // ======================================== Functions For entity BertNer ================================================================
const triggerEntityExtraction = async (caseId: string | null) => {
  if (!caseId) {
    console.log("No caseId available for entity extraction");
    return;
  }

  try {
    console.log("🔍 Starting entity extraction for caseId:", caseId);
    setIsExtractingEntities(true);
    setStage("followup"); // Keep the stage as followup
    
    const authService = new AuthService();
    const accessToken = await authService.getAccessToken();

    const response = await axios.post(
      `${API_BASE_URL}/extract-entities/`,
      {
        caseId: caseId,
        patientId: patientId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log("✅ Entity extraction response:", response.data);

    if (response.data.success) {
      setExtractedEntities(response.data.entities || []);
      setEntityExtractionComplete(true);
      console.log(`✅ Extracted ${response.data.count} unique entities`);
    } else {
      console.error("Entity extraction failed:", response.data.error);
      setExtractedEntities([]);
      setEntityExtractionComplete(true);
    }
  } catch (error) {
    console.error("Entity Extraction Error:", error);
    setExtractedEntities([]);
    setEntityExtractionComplete(true);
  } finally {
    setIsExtractingEntities(false);
    // Mark analysis as complete after entity extraction
    setTimeout(() => {
      setAnalysisComplete(true);
      console.log("Analysis fully complete including entity extraction");
    }, 100);
  }
};

// 
const triggerStructuredExtraction = async (caseId: string | null) => {
  if (!caseId) {
    console.warn("⚠️ No caseId for structured extraction");
    return;
  }

  console.log("🔬 Starting structured extraction for case:", caseId);
  setIsExtractingStructured(true);
  setStructuredExtractionComplete(false);

  try {
    const authService = new AuthService();
    const accessToken = await authService.getAccessToken();

    const response = await axios.post(
      `${API_BASE_URL}/extract-structured/`,
      {
        caseId: caseId,
        patientId: patientId || undefined,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log("✅ Structured extraction response:", response.data);

    if (response.data.success) {
      setStructuredExtraction(response.data.extraction);
      setStructuredExtractionComplete(true);
      console.log(`✅ Extracted structured data from ${response.data.llm_blocks_count} blocks`);
    } else {
      console.error("❌ Structured extraction failed:", response.data.error);
    }
  } catch (error: any) {
    console.error("❌ Error during structured extraction:", error);
  } finally {
    setIsExtractingStructured(false);
  }
};

// Update the existing useEffect to trigger structured extraction after entity extraction
useEffect(() => {
  if (entityExtractionComplete && caseId && !structuredExtractionComplete) {
    console.log("🔄 Entity extraction complete, triggering structured extraction...");
    triggerStructuredExtraction(caseId);
  }
}, [entityExtractionComplete, caseId, structuredExtractionComplete]);



  // Check and enumerate available cameras
  const enumerateCameras = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        console.log("enumerateDevices() not supported");
        return [];
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      console.log("Available cameras:", cameras);
      setAvailableCameras(cameras);
      return cameras;
    } catch (err) {
      console.error("Error enumerating devices:", err);
      return [];
    }
  };

  // Camera handlers
  const openCamera = async () => {
    try {
      setCameraLoading(true);
      setCameraError(null);
      setCameraOpen(true); // Open dialog immediately to show loading state

      // Get available cameras first
      const cameras = await enumerateCameras();
      
      // Determine which camera to use
      let constraints: MediaStreamConstraints = { audio: false, video: true };
      
      if (cameras.length > 0) {
        // If we have specific device IDs, use them
        const cameraToUse = cameras[currentCameraIndex];
        if (cameraToUse?.deviceId) {
          constraints = {
            audio: false,
            video: {
              deviceId: { exact: cameraToUse.deviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          };
          console.log(`Using camera ${currentCameraIndex + 1}/${cameras.length} with ID: ${cameraToUse.deviceId}`);
        }
      } else {
        // Fall back to facingMode if no device IDs are available
        const preferredFacingMode = isMobile ? "environment" : "user";
        constraints = {
          audio: false,
          video: {
            facingMode: frontCamera ? "user" : preferredFacingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        console.log("Using facingMode constraint:", constraints.video);
      }
      
      console.log(`Camera init attempt ${cameraInitAttempts + 1} with constraints:`, constraints);
      
      // For Safari compatibility, explicitly set timeouts
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Camera access timeout")), 10000);
      });
      
      // Try to get the media stream with timeout
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        timeoutPromise
      ]) as MediaStream;
      
      // If we reach here, we have a stream
      setCameraStream(stream);
      
      if (videoRef.current) {
        // In some browsers, setting srcObject can be asynchronous
        videoRef.current.srcObject = stream;
        
        // Force a reload of the video element
        videoRef.current.load();
        
        // Add event listeners to handle when video is ready
        videoRef.current.onloadedmetadata = () => {
          console.log("Video metadata loaded");
          if (videoRef.current) {
            videoRef.current.play()
              .then(() => {
                console.log("Video playback started");
                setCameraLoading(false);
                setCameraInitAttempts(0); // Reset attempts on success
              })
              .catch(err => {
                console.error("Error playing video:", err);
                setCameraError("Failed to start video playback. Please try Switch Camera.");
                setCameraLoading(false);
              });
          }
        };
        
        videoRef.current.onerror = () => {
          console.error("Video element error");
          setCameraError("Video element encountered an error. Please try Switch Camera.");
          setCameraLoading(false);
        };
        
        // Set a timeout in case the onloadedmetadata event never fires
        setTimeout(() => {
          if (cameraLoading) {
            console.log("Camera init timed out - attempting to force play");
            if (videoRef.current) {
              videoRef.current.play()
                .then(() => {
                  console.log("Forced video playback started");
                  setCameraLoading(false);
                })
                .catch(err => {
                  console.error("Forced play error:", err);
                  // Don't set error here as we'll try the fallback
                  tryFallbackInitialization();
                });
            }
          }
        }, 3000);
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      let errorMessage = "Could not access camera. Please try the Switch Camera button or check permissions.";
      
      // More specific error messages based on the error
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage = "Camera access was denied. Please allow camera permissions in your browser settings.";
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage = "No camera found on your device or it's being used by another application.";
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage = "Camera is in use by another application or is not accessible.";
        } else if (err.name === 'OverconstrainedError') {
          errorMessage = "Camera doesn't support the requested settings. Try the Switch Camera button.";
        } else if (err.name === 'AbortError') {
          errorMessage = "Camera access was aborted. Please try again or use Switch Camera.";
        }
      }
      
      setCameraError(errorMessage);
      setCameraLoading(false);
      
      // If this is the first attempt, try a fallback
      if (cameraInitAttempts < 2) {
        tryFallbackInitialization();
      }
    }
  };

  // Fallback camera initialization with simpler constraints
  const tryFallbackInitialization = () => {
    setCameraInitAttempts(prev => prev + 1);
    setTimeout(() => {
      console.log("Trying fallback camera initialization");
      // Toggle frontCamera which will trigger different constraints
      setFrontCamera(prev => !prev);
      // Re-attempt with new settings after a short delay
      setTimeout(() => {
        if (cameraOpen) {
          switchCamera();
        }
      }, 500);
    }, 1000);
  };

  const switchCamera = async () => {
    try {
      setCameraLoading(true);
      
      // Close existing stream
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      if (availableCameras.length > 1) {
        // If we have multiple cameras, cycle through them
        setCurrentCameraIndex(prevIndex => (prevIndex + 1) % availableCameras.length);
      } else {
        // If we don't have specific cameras enumerated, just toggle facingMode
        setFrontCamera(prev => !prev);
      }
      
      // Small delay before reopening camera with new settings
      setTimeout(() => {
        openCamera();
      }, 300);
      
    } catch (err) {
      console.error("Error switching camera:", err);
      setCameraError("Failed to switch camera. Please try again or use file upload instead.");
      setCameraLoading(false);
    }
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert to file
        canvas.toBlob((blob) => {
          if (blob) {
            const fileName = `camera_capture_${Date.now()}.jpg`;
            const file = new File([blob], fileName, { type: 'image/jpeg' });
            
            // Update state
            setSelectedFileName(fileName);
            setImagePreviewUrl(URL.createObjectURL(blob));
            
            // Update the file input for form submission
            if (fileInputRef.current) {
              const dataTransfer = new DataTransfer();
              dataTransfer.items.add(file);
              fileInputRef.current.files = dataTransfer.files;
            }
            
            // Close camera
            closeCamera();
          }
        }, 'image/jpeg', 0.9); // Use 90% quality for JPEG
      }
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoading && !analysisComplete && canUploadMore) {
      setIsDragActive(true);
    }
  }, [isLoading, analysisComplete, canUploadMore]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoading && !analysisComplete && canUploadMore) {
      setIsDragActive(true);
    }
  }, [isLoading, analysisComplete, canUploadMore]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    if (!isLoading && !analysisComplete && canUploadMore && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type.startsWith('image/')) {
        setSelectedFileName(droppedFile.name);
        setImagePreviewUrl(URL.createObjectURL(droppedFile));
        
        // Update the file input for form submission
        if (fileInputRef.current) {
          // Create a DataTransfer object to set files
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(droppedFile);
          fileInputRef.current.files = dataTransfer.files;
        }
      } else {
        alert("Please upload an image file.");
      }
    }
  }, [isLoading, analysisComplete, canUploadMore]);

  const triggerSegmentation = async (
    imageName: string,
    caseId: string | null,
    patientId: string | null
  ) => {
    try {
      const authService = new AuthService();
      const accessToken = await authService.getAccessToken();
      setIsLoading(true);
      setStage("segmented");
      const response = await axios.post(`${API_BASE_URL}/segment/`, {
        question: imageName,
        caseId: caseId,
        patientId: patientId,
      },{
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      
      // ============= CHECK FOR VALIDATION FAILURE =============
      if (response.data?.success === false && response.data?.error === "segmentation_validation_failed") {
        console.error("❌ Segmentation validation failed:", response.data);
        
        // Build user-friendly error message
        const userGuidance = response.data.user_guidance || [];
        const confidence = response.data.confidence || 0;
        
        // Create formatted message for chat
        const errorMessage = `
⚠️ **Segmentation Quality Issue**

The automated image analysis was unable to reliably segment the lesion from your image (confidence: ${(confidence * 100).toFixed(0)}%).

**Please try again with an improved image:**

${userGuidance.map((line: string) => `• ${line}`).join('\n')}

**Tips for best results:**
• Use good, even lighting
• Center the lesion (should occupy 15-50% of frame)
• Ensure the entire lesion is visible
• Keep the camera steady and in focus
• Minimize shadows and background clutter
        `.trim();
        
        // Add error message to chat history
        setChatHistory((prev) => [
          ...prev,
          {
            role: "bot",
            text: errorMessage
          }
        ]);
        
        // Reset to start state so user can try again
        setStage("start");
        setFile(null);
        setImagePreviewUrl(null);
        setSelectedFileName("");
        setCaseId(null);
        setAnalysisComplete(false);
        setIsLoading(false);
        
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        
        return; // Stop here, don't proceed to XAI
      }
      
      // ============= VALIDATION PASSED - CONTINUE =============
      if (response.data?.response) {
        setReturnedSegment(response.data.response);
        const { paragraphContent } = extractContent(response.data.response);
        await triggerXAI(`${paragraphContent}${xaibot}`, caseId);
      } else {
        // If no response data, still mark as complete
        setAnalysisComplete(true);
      }
    } catch (error) {
      console.error("Segmentation Error:", error);
      
      // Check if error response contains validation failure info
      if (axios.isAxiosError(error) && error.response?.data?.error === "segmentation_validation_failed") {
        const errorData = error.response.data;
        const userGuidance = errorData.user_guidance || ["Please try again with a clearer image"];
        
        const errorMessage = `
⚠️ **Image Quality Issue**

${errorData.message || "Could not process the image"}

**Please adjust and re-upload:**
${userGuidance.map((line: string) => `• ${line}`).join('\n')}
        `.trim();
        
        setChatHistory((prev) => [
          ...prev,
          {
            role: "bot",
            text: errorMessage
          }
        ]);
        
        // Reset to allow retry
        setStage("start");
        setFile(null);
        setImagePreviewUrl(null);
        setSelectedFileName("");
        setCaseId(null);
      } else {
        // Generic error
        setChatHistory((prev) => [
          ...prev,
          {
            role: "bot",
            text: "❌ An error occurred during image processing. Please try again with a different image or check your connection."
          }
        ]);
      }
      
      // Mark as complete so reset is possible
      setAnalysisComplete(true);
    } finally {
      setIsLoading(false);
    }
  };

const triggerXAI = async (xaiInput: string, caseId: string | null) => {
  try {
    setIsLoading(true);
    setStage("explained");
    setXairesponse("");
    const authService = new AuthService();
    const accessToken = await authService.getAccessToken();

    const response = await fetch(`${API_BASE_URL}/xai/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ question: xaiInput, caseId: caseId, patientId }),
    });

    const reader = response.body?.getReader();
    if (!reader) throw new Error("No reader available");
    const decoder = new TextDecoder("utf-8");

    let fullResponse = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done || !value) break;
      const chunk = decoder.decode(value, { stream: true });
      fullResponse += chunk;
      setXairesponse((prev) => prev + chunk);
    }

    if (fullResponse.includes("N/A")) {
      console.log("XAI found N/A, triggering followUp");
      setFollowup(fullResponse);
      await triggerFollowUp(fullResponse, caseId);
      // Entity extraction will be triggered after followUp completes
    } else {
      console.log("XAI complete without N/A, triggering entity extraction");
      // Trigger entity extraction directly
      await triggerEntityExtraction(caseId);
    }
  } catch (err) {
    console.error("XAI Error:", err);
    setAnalysisComplete(true);
  } finally {
    setIsLoading(false);
  }
};


// Update the triggerFollowUp function (around line 560)
const triggerFollowUp = async (
  followupText: string,
  caseId: string | null
) => {
  try {
    setIsLoading(true);
    setStage("followup");
    setreturnedFollowUp("");
    const authService = new AuthService();
    const accessToken = await authService.getAccessToken();

    const response = await fetch(`${API_BASE_URL}/followUp/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ question: followupText, caseId: caseId }),
    });
    
    const reader = response.body?.getReader();
    if (!reader) throw new Error("No reader available");
    const decoder = new TextDecoder("utf-8");
    let fullText = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done || !value) break;
      const chunk = decoder.decode(value, { stream: true });
      fullText += chunk;
      setreturnedFollowUp((prev) => prev + chunk);
    }

    console.log("Follow-up complete, triggering entity extraction");
    // Trigger entity extraction after follow-up completes
    await triggerEntityExtraction(caseId);
  } catch (error) {
    console.error("Follow-up Error:", error);
    setAnalysisComplete(true);
  } finally {
    setIsLoading(false);
  }
};


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setSelectedFileName(selectedFile.name);
      
      // Create preview
      setImagePreviewUrl(URL.createObjectURL(selectedFile));
    } else {
      setSelectedFileName("");
      setImagePreviewUrl(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fileInput = e.currentTarget.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement | null;
    if (!fileInput?.files?.length) return;

    const selectedFile = fileInput.files[0];
    const fileExtension = selectedFile.name.split(".").pop();
    const randomFileName = `image_${Math.random()
      .toString(36)
      .substring(2, 15)}_${Date.now()}.${fileExtension}`;

    // Create a new File object with randomized name
    const renamedFile = new File([selectedFile], randomFileName, {
      type: selectedFile.type,
    });

    setFile(renamedFile);
    setStage("start");
    setChatHistory([]);
    setFollowup("");
    setXairesponse("");
    setReturnedSegment("");
    setreturnedFollowUp("");
    setXaiBot("");
    setCaseId(null);
    setAnalysisStarted(true);
    setAnalysisComplete(false);

    try {
      setIsLoading(true);
      setStage("uploaded");
      const authService = new AuthService();
      const accessToken = await authService.getAccessToken();

      const formData = new FormData();
      formData.append("file", renamedFile);
      if (patientId) {
        formData.append("patientId", patientId);
      }

      const response = await fetch(`${API_BASE_URL}/uploadfile/`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No reader available");
      const decoder = new TextDecoder("utf-8");

      let botResponse = "";
      let firstChunk = true;
      let extractedCaseId = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done || !value) break;
        const chunk = decoder.decode(value, { stream: true });

        if (firstChunk) {
          // Check for "caseId::" prefix in the first chunk
          if (chunk.includes("caseId::")) {
            const parts = chunk.split("caseId::");
            if (parts.length >= 2) {
              // Extract the ID and remove any newlines or spaces
              const idPart = parts[1].trim();
              extractedCaseId = idPart.split(/[\n\s]/)[0];
              setCaseId(extractedCaseId);

              // Remove the caseId part from the chunk
              const modifiedChunk = chunk
                .replace(`caseId::${extractedCaseId}`, "")
                .trim();
              botResponse += modifiedChunk;

              // Update chat history without the caseId
              if (modifiedChunk) {
                setChatHistory((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === "bot") last.text += modifiedChunk;
                  else updated.push({ role: "bot", text: modifiedChunk });
                  return updated;
                });
              }

              firstChunk = false;
              continue;
            }
          }
        }

        botResponse += chunk;

        setChatHistory((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "bot") last.text += chunk;
          else updated.push({ role: "bot", text: chunk });
          return updated;
        });
      }

      const fullResponse = botResponse.trim();
      setXaiBot(fullResponse);
      
      if (!fullResponse.includes("N/A")) {
        await triggerSegmentation(renamedFile.name, extractedCaseId, patientId);
      } else {
        setStage("followup");
        await triggerFollowUp(fullResponse, extractedCaseId);
      }
    } catch (error) {
      console.error("Upload Error:", error);
      // Mark analysis as complete if there was an error in upload
      setAnalysisComplete(true);
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoadingSkeletons = () => (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1, mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 1, mb: 2 }} />
      <Skeleton variant="rectangular" width="95%" height={80} sx={{ borderRadius: 1, mb: 1 }} />
      <Skeleton variant="rectangular" width="90%" height={40} sx={{ borderRadius: 1 }} />
    </Box>
  );

  // Add state for tour guide
  const [runTour, setRunTour] = useState<boolean>(false);

  // Define tour steps for the chat domain
  const tourSteps = [
    {
      target: '[data-tour="upload-section"]',
      content: 'Start by selecting an image to analyze using one of these methods: Upload, Camera, or Drag & Drop',
      disableBeacon: true,
    },
    {
      target: '[data-tour="analysis-button"]',
      content: 'After selecting an image, click here to begin the analysis',
      disableBeacon: true,
    },
    {
      target: '[data-tour="accordions"]',
      content: 'Results will appear in these expandable sections - click to view the detailed analysis for each stage',
      disableBeacon: true,
    },
    {
      target: '[data-tour="image-preview"]',
      content: 'Your uploaded image will appear here for reference during analysis',
      disableBeacon: true,
    },
    {
      target: '[data-tour="patient-chip"]',
      content: 'This shows the Patient ID the analysis is associated with',
      disableBeacon: true,
    }
  ];

  const handleStartTour = (e: React.MouseEvent) => {
    e.preventDefault();
    // Reset the tour completion status in localStorage
    localStorage.removeItem("tour_chatdomain_completed");
    localStorage.removeItem("tour_chatdomain_step");
    // Force tour to stop and then start again (important for restarting)
    setRunTour(false);
    // Use setTimeout to ensure React has time to process the false state before setting to true
    setTimeout(() => setRunTour(true), 50);
  };

  const handleFinishTour = () => {
    setRunTour(false);
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraOpen(false);
    setCameraLoading(false);
    setCameraError(null);
  };

  // Add navigation handler for Cases link
  const handleCasesNavigation = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/images");
  };
  
  return (
    <Card
      elevation={3}
      sx={{
        borderRadius: { xs: 2, sm: 3 },
        overflow: "hidden",
        transition: "all 0.3s ease",
        background: isDarkMode
          ? "linear-gradient(to bottom, rgba(35,35,40,0.9), rgba(25,25,30,1))"
          : "linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(250,250,255,1))",
        boxShadow: isDarkMode
          ? "0 8px 32px rgba(0,0,0,0.3)"
          : "0 8px 32px rgba(100,100,255,0.1)",
        maxWidth: "100%",
        mx: "auto",
        position: "relative",
      }}
    >
      {patientId && (
        <Fade in={true} timeout={800}>
          <Chip
            label={`Patient ID: ${patientId}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              fontSize: "0.7rem",
              opacity: 0.8,
              "&:hover": {
                opacity: 1,
              },
            }}
            data-tour="patient-chip"
          />
        </Fade>
      )}

      <CardContent
        sx={{
          p: { xs: 2, sm: 1, md: 4 },
          pb: { xs: 1, sm: 2 },
        }}
      >
        {stage === "start" && !analysisStarted && (
          <Fade in={true} timeout={800}>
            <Box>
              <HeroLanding />
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  onClick={handleStartTour}
                  startIcon={<SchoolIcon />}
                  variant="contained"
                  color="secondary"
                  sx={{
                    backgroundColor: "#e91e63",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#d81b60",
                    },
                  }}
                >
                  Guide: AI Analysis
                </Button>
              </Box>
            </Box>
          </Fade>
        )}

        {analysisStarted && (
          <Box
            sx={{
              mb: 3,
              mt: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              pb: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant={isSmallScreen ? "h6" : "h5"}
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AssignmentIcon fontSize={isSmallScreen ? "medium" : "large"} />
                Skin Analysis
              </Typography>

              {!isLoading && stage === "start" && (
                <Button
                  onClick={handleStartTour}
                  startIcon={<SchoolIcon />}
                  variant="contained"
                  color="secondary"
                  size="small"
                  sx={{
                    backgroundColor: "#e91e63",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#d81b60",
                    },
                  }}
                >
                  Guide
                </Button>
              )}
            </Stack>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: { xs: "center", sm: "flex-end" },
              }}
            >
              {stage === "uploaded" && !isExtractingEntities && (
                <Chip
                  label="Initial Analysis"
                  color="primary"
                  variant="filled"
                  size={isSmallScreen ? "small" : "medium"}
                />
              )}
              {stage === "segmented" && !isExtractingEntities && (
                <Chip
                  label="Segmentation"
                  color="secondary"
                  variant="filled"
                  size={isSmallScreen ? "small" : "medium"}
                />
              )}
              {stage === "explained" && !isExtractingEntities && (
                <Chip
                  label="AI Explanation"
                  color="info"
                  variant="filled"
                  size={isSmallScreen ? "small" : "medium"}
                />
              )}
              {stage === "followup" && !isExtractingEntities && (
                <Chip
                  label="Follow-up Analysis"
                  color="success"
                  variant="filled"
                  size={isSmallScreen ? "small" : "medium"}
                />
              )}
              {/* Entity extraction chip - shows regardless of stage */}
              {isExtractingEntities && (
                <Chip
                  label="Extracting Entities"
                  color="warning"
                  variant="filled"
                  size={isSmallScreen ? "small" : "medium"}
                  icon={
                    <CircularProgress size={16} sx={{ color: "inherit" }} />
                  }
                />
              )}
              {/* Structured extraction chip */}
              {isExtractingStructured && (
                <Chip
                  label="Extracting Clinical Data"
                  color="info"
                  variant="filled"
                  size={isSmallScreen ? "small" : "medium"}
                  icon={
                    <CircularProgress size={16} sx={{ color: "inherit" }} />
                  }
                />
              )}
              {isLoading && !isExtractingEntities && (
                <CircularProgress size={24} sx={{ ml: 1 }} />
              )}
            </Box>
          </Box>
        )}

        <Box data-tour="accordions">
          {chatHistory.length > 0 && (
            <Accordion
              defaultExpanded={false}
              sx={{
                mb: 2,
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: alpha(theme.palette.primary.main, 0.05),
                  borderLeft: `4px solid ${alpha(
                    theme.palette.primary.main,
                    0.7
                  )}`,
                }}
              >
                <Typography
                  variant={isSmallScreen ? "subtitle1" : "h6"}
                  sx={{ fontWeight: 500 }}
                >
                  Initial Evaluation
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ChatHistory chatHistory={chatHistory} isLoading={isLoading} />
              </AccordionDetails>
            </Accordion>
          )}

          {returnedSegment && (
            <Accordion
              defaultExpanded={false}
              sx={{
                mb: 2,
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: alpha(theme.palette.secondary.main, 0.05),
                  borderLeft: `4px solid ${alpha(
                    theme.palette.secondary.main,
                    0.7
                  )}`,
                }}
              >
                <Typography
                  variant={isSmallScreen ? "subtitle1" : "h6"}
                  sx={{ fontWeight: 500 }}
                >
                  Segmentation Analysis
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SegmentOutput
                  chatSegment={returnedSegment}
                  isLoading={isLoading}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {xairesponse && (
            <Accordion
              defaultExpanded={true}
              sx={{
                mb: 2,
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: alpha(theme.palette.info.main, 0.05),
                  borderLeft: `4px solid ${alpha(
                    theme.palette.info.main,
                    0.7
                  )}`,
                }}
              >
                <Typography
                  variant={isSmallScreen ? "subtitle1" : "h6"}
                  sx={{ fontWeight: 500 }}
                >
                  Explainable AI Output
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <XaiOutput chatXai={xairesponse} isLoading={isLoading} />
              </AccordionDetails>
            </Accordion>
          )}

          {returnedFollowUp && (
            <Accordion
              defaultExpanded={true}
              sx={{
                mb: 2,
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: alpha(theme.palette.success.main, 0.05),
                  borderLeft: `4px solid ${alpha(
                    theme.palette.success.main,
                    0.7
                  )}`,
                }}
              >
                <Typography
                  variant={isSmallScreen ? "subtitle1" : "h6"}
                  sx={{ fontWeight: 500 }}
                >
                  Follow-up Evaluation
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FollowUpOutput
                  chatFollowUp={returnedFollowUp}
                  isLoading={isLoading}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {isExtractingEntities && (
            <Fade in={true} timeout={500}>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={24} color="warning" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Extracting medical entities from analysis...
                </Typography>
              </Box>
            </Fade>
          )}

          {/* NEW: Extracted Medical Entities Accordion */}
          {entityExtractionComplete && extractedEntities.length > 0 && (
            <Accordion
              defaultExpanded={true}
              sx={{
                mb: 2,
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: alpha(theme.palette.warning.main, 0.05),
                  borderLeft: `4px solid ${alpha(
                    theme.palette.warning.main,
                    0.7
                  )}`,
                }}
              >
                <Typography
                  variant={isSmallScreen ? "subtitle1" : "h6"}
                  sx={{
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🧬 Extracted Medical Entities From text (Beta)
                  <Chip
                    label={extractedEntities.length}
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    AI-detected dermatological conditions from the complete
                    analysis, please advise with your clinical expertise:
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    {extractedEntities.map((entity, index) => (
                      <Chip
                        key={index}
                        label={entity.entity}
                        variant="outlined"
                        color="warning"
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          py: 2,
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.warning.main,
                              0.1
                            ),
                            transform: "scale(1.05)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      />
                    ))}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    This is a Intelligent decision support system made for
                    doctors not a diagnostic tool.
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Loading state for Structured Extraction */}
          {isExtractingStructured && (
            <Fade in={true} timeout={500}>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={24} color="info" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Extracting structured clinical data and building knowledge graph...
                </Typography>
              </Box>
            </Fade>
          )}

          {structuredExtractionComplete && structuredExtraction && (
            <>
              {/* Existing Structured Clinical Assessment Accordion */}
              <Accordion
                defaultExpanded={true}
                sx={{
                  mb: 2,
                  borderRadius: 1,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: alpha(theme.palette.info.main, 0.05),
                    borderLeft: `4px solid ${alpha(theme.palette.info.main, 0.7)}`,
                  }}
                >
                  <Typography
                    variant={isSmallScreen ? "subtitle1" : "h6"}
                    sx={{
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📋 Structured Clinical Assessment
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Primary Diagnosis */}
                    <Paper elevation={1} sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                        Primary Diagnosis
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold", mt: 1 }}>
                        {structuredExtraction.primary_diagnosis}
                      </Typography>
                    </Paper>

                    {/* Malignancy Risk */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Malignancy Risk:
                      </Typography>
                      <Chip
                        label={structuredExtraction.malignancy_risk.toUpperCase()}
                        color={
                          structuredExtraction.malignancy_risk === "benign"
                            ? "success"
                            : structuredExtraction.malignancy_risk === "malignant"
                            ? "error"
                            : structuredExtraction.malignancy_risk === "premalignant"
                            ? "warning"
                            : "default"
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>

                    {/* Differential Diagnoses */}
                    {structuredExtraction.differential_diagnoses?.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Differential Diagnoses:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {structuredExtraction.differential_diagnoses.map(
                            (dx: string, idx: number) => (
                              <Chip
                                key={idx}
                                label={dx}
                                variant="outlined"
                                color="primary"
                                size="small"
                              />
                            )
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Finding Description */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Finding Description:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {structuredExtraction.finding_description}
                      </Typography>
                    </Box>

                    {/* Characteristics */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Characteristics:
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Color:
                          </Typography>
                          <Typography variant="body2">
                            {structuredExtraction.characteristics.color}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Size:
                          </Typography>
                          <Typography variant="body2">
                            {structuredExtraction.characteristics.size}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Shape:
                          </Typography>
                          <Typography variant="body2">
                            {structuredExtraction.characteristics.shape}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Texture:
                          </Typography>
                          <Typography variant="body2">
                            {structuredExtraction.characteristics.texture}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Location */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Location:
                      </Typography>
                      <Typography variant="body2">
                        {structuredExtraction.finding_location}
                      </Typography>
                    </Box>

                    {/* Risk Factors */}
                    {structuredExtraction.risk_factors?.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Risk Factors:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {structuredExtraction.risk_factors.map(
                            (rf: string, idx: number) => (
                              <li key={idx}>
                                <Typography variant="body2">{rf}</Typography>
                              </li>
                            )
                          )}
                        </ul>
                      </Box>
                    )}

                    {/* Recommendations */}
                    {structuredExtraction.recommendations?.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Clinical Recommendations:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {structuredExtraction.recommendations.map(
                            (rec: string, idx: number) => (
                              <li key={idx}>
                                <Typography variant="body2">{rec}</Typography>
                              </li>
                            )
                          )}
                        </ul>
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      This structured assessment is automatically extracted from the complete
                      analysis using AI. Please verify all information clinically.
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* NEW: Knowledge Graph Accordion */}
              <Accordion
                defaultExpanded={true}
                sx={{
                  mb: 2,
                  borderRadius: 1,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                    borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.7)}`,
                  }}
                >
                  <Typography
                    variant={isSmallScreen ? "subtitle1" : "h6"}
                    sx={{
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    🧠 InsightGraph (Pilot)
                    <Chip
                      label="Interactive"
                      size="small"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Interactive visualization of the extracted clinical relationships.
                      Click nodes to zoom, hover for details.
                    </Typography>
                    
                    <KnowledgeGraph structuredData={structuredExtraction} />
                    
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        💡 <strong>Tip:</strong> This knowledge graph shows relationships between diagnosis, 
                        characteristics, risk factors, and recommendations. Larger nodes indicate more critical information.
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>

        {caseId && (
          <Fade in={true} timeout={800}>
            <Chip
              label={`Case ID: ${caseId}`}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{
                mt: 1,
                mb: 1,
                fontSize: "0.7rem",
                opacity: 0.8,
                "&:hover": {
                  opacity: 1,
                },
              }}
            />
          </Fade>
        )}

        {isLoading && renderLoadingSkeletons()}

        {analysisComplete && (
          <Fade in={true} timeout={800}>
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.info.main, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <WarningAmberIcon color="info" />
              <Typography variant="body2">
                Analysis complete. Please use the "Start New Analysis" button
                below to analyze a new image and avoid case misclassification.
                To create a report go to{" "}
                <Box
                  component="span"
                  onClick={handleCasesNavigation}
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: "bold",
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": {
                      color: theme.palette.primary.dark,
                    },
                  }}
                >
                  Cases
                </Box>{" "}
                section and select the Case ID for this analysis.
              </Typography>
            </Box>
          </Fade>
        )}

        <div ref={chatHistoryRef} />
      </CardContent>

      <Divider />

      <CardActions
        sx={{
          p: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box
          width="100%"
          data-tour="upload-section"
          ref={dropZoneRef}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            position: "relative",
            borderRadius: 2,
            border: isDragActive
              ? `2px dashed ${theme.palette.primary.main}`
              : `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
            p: isDragActive ? 1.5 : 0,
            transition: "all 0.2s ease",
            ...(isDragActive && {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            }),
          }}
        >
          {isDragActive && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                zIndex: 1,
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                Drop image here
              </Typography>
            </Box>
          )}

          {!analysisComplete ? (
            <form ref={formRef} onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {/* Free Tier Usage Indicator */}
                {isFreeUser && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: isAtLimit
                        ? alpha(theme.palette.error.main, 0.1) 
                        : isNearLimit
                        ? alpha(theme.palette.warning.main, 0.1)
                        : alpha(theme.palette.info.main, 0.1),
                      border: 1,
                      borderColor: isAtLimit
                        ? 'error.main' 
                        : isNearLimit
                        ? 'warning.main'
                        : 'info.main',
                      borderRadius: 2,
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={600}>
                          Free Tier Usage
                        </Typography>
                        <Chip 
                          label={`${imageCount}/${imageLimit} images`}
                          size="small"
                          color={isAtLimit ? 'error' : isNearLimit ? 'warning' : 'info'}
                        />
                      </Box>
                      <Box>
                        <Box
                          sx={{
                            width: '100%',
                            height: 8,
                            bgcolor: alpha(theme.palette.action.disabledBackground, 0.2),
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${((imageCount || 0) / (imageLimit || 15)) * 100}%`,
                              height: '100%',
                              bgcolor: isAtLimit
                                ? 'error.main' 
                                : isNearLimit
                                ? 'warning.main'
                                : 'primary.main',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {(remaining ?? 0) > 0 
                            ? `${remaining} ${remaining === 1 ? 'image' : 'images'} remaining`
                            : `Limit reached (${imageLimit} images) - upgrade for unlimited uploads`
                          }
                        </Typography>
                      </Box>
                      {isAtLimit && (
                        <>
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              Upload limit reached!
                            </Typography>
                            <Typography variant="caption">
                              Delete old cases to free up space, or upgrade for unlimited uploads.
                            </Typography>
                          </Alert>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button
                              type="button"
                              variant="outlined"
                              size="medium" 
                              onClick={() => navigate('/images')}
                              startIcon={<DeleteIcon />}
                              sx={{ 
                                flex: 1,
                                fontWeight: 600,
                                borderWidth: 2,
                                '&:hover': {
                                  borderWidth: 2
                                }
                              }}
                            >
                              Delete Old Cases
                            </Button>
                            <Button
                              type="button"
                              variant="contained"
                              size="medium"
                              onClick={() => navigate('/account?tab=subscription')}
                              startIcon={<UpgradeIcon />}
                              sx={{ 
                                flex: 1,
                                fontWeight: 600
                              }}
                            >
                              Upgrade Plan
                            </Button>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Paper>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<FileUploadIcon />}
                    disabled={isLoading || !canUploadMore}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                      transition: "all 0.2s ease",
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      "&:hover": {
                        borderColor: theme.palette.primary.dark,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    {canUploadMore ? 'Choose Image' : 'Limit Reached'}
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileChange}
                      disabled={isLoading || !canUploadMore}
                    />
                  </Button>
                  {!isMobile && (
                  <Button
                    variant="outlined"
                    startIcon={<CameraAltIcon />}
                    disabled={isLoading || !canUploadMore}
                    onClick={openCamera}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                      transition: "all 0.2s ease",
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main,
                      "&:hover": {
                        borderColor: theme.palette.secondary.dark,
                        bgcolor: alpha(theme.palette.secondary.main, 0.04),
                      },
                    }}
                  >
                    {canUploadMore ? 'Use Camera' : 'Limit Reached'}
                  </Button>)}

                  {selectedFileName && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: { xs: "100%", sm: "200px" },
                      }}
                    >
                      {selectedFileName}
                    </Typography>
                  )}
                </Box>

                {!selectedFileName && !isDragActive && canUploadMore && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{
                      opacity: 0.7,
                      fontStyle: "italic",
                      mt: 1,
                      mb: 1,
                    }}
                  >
                    or drag and drop image here
                  </Typography>
                )}

                <Box
                  sx={{ display: "flex", gap: 2, justifyContent: "flex-start" }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SendIcon />}
                    disabled={isLoading || !selectedFileName}
                    data-tour="analysis-button"
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      background: `linear-gradient(45deg, ${
                        theme.palette.primary.main
                      } 30%, ${alpha(theme.palette.primary.main, 0.8)} 90%)`,
                      transition: "all 0.3s ease",
                      boxShadow: `0 2px 10px ${alpha(
                        theme.palette.primary.main,
                        0.4
                      )}`,
                      "&:hover": {
                        boxShadow: `0 4px 20px ${alpha(
                          theme.palette.primary.main,
                          0.5
                        )}`,
                        transform: "translateY(-2px)",
                      },
                      "&:disabled": {
                        background: alpha(theme.palette.action.disabled, 0.2),
                      },
                    }}
                  >
                    {isLoading ? "Analyzing..." : "Start Analysis"}
                  </Button>

                  {imagePreviewUrl && !isLoading && (
                    <Tooltip title="Clear selected image">
                      <IconButton
                        onClick={() => {
                          setImagePreviewUrl(null);
                          setSelectedFileName("");
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                        }}
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Stack>
            </form>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RestartAltIcon />}
              onClick={resetAnalysis}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                background: `linear-gradient(45deg, ${
                  theme.palette.secondary.main
                } 30%, ${alpha(theme.palette.secondary.main, 0.8)} 90%)`,
                transition: "all 0.3s ease",
                boxShadow: `0 2px 10px ${alpha(
                  theme.palette.secondary.main,
                  0.4
                )}`,
                "&:hover": {
                  boxShadow: `0 4px 20px ${alpha(
                    theme.palette.secondary.main,
                    0.5
                  )}`,
                  transform: "translateY(-2px)",
                },
              }}
            >
              Start New Analysis
            </Button>
          )}
        </Box>

        {imagePreviewUrl && (
          <Fade in={true} timeout={500}>
            <Paper
              elevation={2}
              sx={{
                p: 1,
                borderRadius: 2,
                width: { xs: "100%", sm: 200 },
                maxWidth: "100%",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
              data-tour="image-preview"
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <ImageIcon fontSize="small" color="primary" />
                Uploaded Image
              </Typography>
              <Box
                sx={{
                  borderRadius: 1,
                  overflow: "hidden",
                  maxHeight: 200,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={imagePreviewUrl}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "200px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Paper>
          </Fade>
        )}
      </CardActions>

      {/* Camera Dialog */}
      <Dialog
        open={cameraOpen}
        onClose={closeCamera}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "#000",
          },
        }}
      >
        <DialogContent
          sx={{ p: 0, position: "relative", height: { xs: 400, sm: 500 } }}
        >
          {cameraLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              }}
            >
              <CircularProgress color="primary" />
              <Typography color="white" sx={{ mt: 2 }}>
                Initializing camera...
              </Typography>
              {cameraInitAttempts > 0 && (
                <Typography
                  color="white"
                  variant="caption"
                  sx={{ mt: 1, maxWidth: "80%", textAlign: "center" }}
                >
                  {cameraInitAttempts === 1
                    ? "First attempt failed. Trying alternate camera..."
                    : "Still having trouble. Try the Switch Camera button."}
                </Typography>
              )}
            </Box>
          )}

          {cameraError && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                p: 3,
              }}
            >
              <WarningAmberIcon color="error" sx={{ fontSize: 40, mb: 2 }} />
              <Typography
                color="error"
                variant="h6"
                align="center"
                gutterBottom
              >
                Camera Error
              </Typography>
              <Typography color="white" align="center" sx={{ mb: 2 }}>
                {cameraError}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={closeCamera}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={switchCamera}
                >
                  Try Switch Camera
                </Button>
              </Stack>
            </Box>
          )}

          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              backgroundColor: "#000",
            }}
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {!cameraLoading && !cameraError && availableCameras.length > 1 && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                backgroundColor: "rgba(0,0,0,0.6)",
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              <Typography variant="caption" color="white">
                Camera {currentCameraIndex + 1}/{availableCameras.length}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "space-between",
            bgcolor: "#000",
            p: 2,
          }}
        >
          <Button
            onClick={closeCamera}
            variant="text"
            startIcon={<CloseIcon />}
            sx={{ color: "#fff" }}
          >
            Cancel
          </Button>

          <Button
            onClick={switchCamera}
            color="info"
            variant="contained"
            sx={{ color: "#fff" }}
            endIcon={
              availableCameras.length > 1 ? (
                <Typography variant="caption" component="span">
                  ({currentCameraIndex + 1}/{availableCameras.length})
                </Typography>
              ) : undefined
            }
          >
            Switch Camera
          </Button>

          <Button
            onClick={captureImage}
            variant="contained"
            startIcon={<PhotoCameraIcon />}
            color="primary"
            disabled={cameraLoading || !!cameraError}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Tour Guide component */}
      <TourGuide
        steps={tourSteps}
        tourId="chatdomain"
        run={runTour}
        continuous={true}
        showSkipButton={true}
        showProgress={true}
        onFinish={handleFinishTour}
        onSkip={handleFinishTour}
      />
    </Card>
  );
}